/**
 * TrainingTimeline — Right sidebar showing completed tricks + current trick during training.
 *
 * Clicking a completed trick shows it on the board.
 * Clicking the current trick returns to live play.
 */

import { useEffect, useRef, useMemo } from 'react';
import MiniCard from './MiniCard';

type ScreenPosition = 'bottom' | 'top' | 'left' | 'right';

interface TrickData {
  trickNumber: number;
  cards: Array<{ card: number; player: number }>;
  winner?: number;
}

interface TrainingTimelineProps {
  tricks: TrickData[];
  currentTrickCards: Array<{ card: number; player: number }>;
  currentTrickNumber: number;
  selectedTrickNumber: number | null;
  selectedPhase?: 'chien' | 'ecart' | 'bids' | null;
  myPosition: number;
  onTrickClick?: (trickNumber: number) => void;
  onCurrentTrickClick?: () => void;
  onPhaseClick?: (type: 'chien' | 'ecart' | 'bids') => void;
  onTrickContextMenu?: (trickNumber: number, e: React.MouseEvent) => void;
  onBidContextMenu?: (bidIndex: number, player: number, bid: number, e: React.MouseEvent) => void;
  bids?: Array<{ player: number; bid: number }>;
  chien?: number[] | null;
  ecartCards?: number[];
  taker?: number | null;
  phase?: string;
  players?: Array<{ pseudo: string; position: number }>;
  highestBid?: number;
  /** Clés `${trickNumber}-${positionInTrick}-${card}` — halo sur les mini-cartes */
  metricHighlightKeys?: Set<string>;
  /** Plis contenant au moins une carte drill — halo sur la ligne du pli */
  metricHighlightTrickNumbers?: Set<number>;
  /** Plis de bifurcation (exercice erreurs) — marqueur dédié */
  bifurcationTrickNumbers?: Set<number>;
}

function getScreenPositions(myPosition: number): Map<number, ScreenPosition> {
  const layout: ScreenPosition[] = ['bottom', 'right', 'top', 'left'];
  const positions = new Map<number, ScreenPosition>();
  for (let i = 0; i < 4; i++) {
    const relativePos = (i - myPosition + 4) % 4;
    positions.set(i, layout[relativePos]);
  }
  return positions;
}

function getThumbnailClass(pos: ScreenPosition): string {
  switch (pos) {
    case 'bottom': return 'thumb-south';
    case 'top': return 'thumb-north';
    case 'left': return 'thumb-west';
    case 'right': return 'thumb-east';
    default: return 'thumb-south';
  }
}

function bidLabel(bid: number): string {
  switch (bid) {
    case 0: return 'Passe';
    case 1: return 'Petite';
    case 2: return 'Garde';
    case 3: return 'G. sans';
    case 4: return 'G. contre';
    default: return String(bid);
  }
}

export default function TrainingTimeline({
  tricks, currentTrickCards, currentTrickNumber, selectedTrickNumber,
  selectedPhase, myPosition, onTrickClick, onCurrentTrickClick, onPhaseClick,
  onTrickContextMenu, onBidContextMenu,
  bids, chien, ecartCards, phase, players,
  metricHighlightKeys, metricHighlightTrickNumbers, bifurcationTrickNumbers,
}: TrainingTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);

  const screenPositions = useMemo(() => getScreenPositions(myPosition), [myPosition]);

  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [tricks.length, currentTrickCards.length, selectedTrickNumber, selectedPhase]);

  const showCurrentTrick = currentTrickNumber > 0;

  const hasBids = bids && bids.length > 0;
  const hasChien = chien && chien.length > 0;
  const hasEcart = ecartCards && ecartCards.length > 0;
  const biddingDone = phase !== 'bidding' && hasBids;

  if (tricks.length === 0 && !showCurrentTrick && !biddingDone) {
    return (
      <div className="training-timeline">
        <div className="replay-timeline">
          <div className="replay-timeline-scroll" style={{ padding: 12, textAlign: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted, rgba(255,255,255,0.4))' }}>
              Les plis complétés apparaîtront ici
            </span>
          </div>
        </div>
      </div>
    );
  }

  const isReviewing = selectedTrickNumber !== null || selectedPhase !== null;

  return (
    <div className="training-timeline">
      <div className="replay-timeline">
        <div className="replay-timeline-scroll" ref={scrollRef}>
          {/* Phase entries: Enchères, Chien, Écart */}
          {biddingDone && (
            <div
              ref={selectedPhase === 'bids' ? activeRef : undefined}
              className={`replay-timeline-item training-timeline-phase ${selectedPhase === 'bids' ? 'active' : ''}`}
              style={{ cursor: onPhaseClick ? 'pointer' : undefined }}
              onClick={() => onPhaseClick?.('bids')}
            >
              <span className="replay-timeline-item-label">Enchères</span>
              <div className="training-timeline-phase-detail">
                {bids!.map((b, idx) => {
                  if (b.bid <= 0) return null;
                  const name = players?.find(p => p.position === b.player)?.pseudo ?? `J${b.player}`;
                  return (
                    <span
                      key={idx}
                      className={`training-timeline-bid ${b.bid > 0 ? 'bid-active' : ''}`}
                      onContextMenu={onBidContextMenu ? (e) => { e.preventDefault(); e.stopPropagation(); onBidContextMenu(idx, b.player, b.bid, e); } : undefined}
                    >
                      {name}: {bidLabel(b.bid)}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {biddingDone && hasChien && (
            <div
              ref={selectedPhase === 'chien' ? activeRef : undefined}
              className={`replay-timeline-item training-timeline-phase ${selectedPhase === 'chien' ? 'active' : ''}`}
              style={{ cursor: onPhaseClick ? 'pointer' : undefined }}
              onClick={() => onPhaseClick?.('chien')}
            >
              <span className="replay-timeline-item-label">Chien</span>
              <div className="replay-timeline-special">
                {chien!.map((card, idx) => (
                  <MiniCard key={`chien-${card}-${idx}`} card={card} />
                ))}
              </div>
            </div>
          )}
          {hasEcart && (
            <div
              ref={selectedPhase === 'ecart' ? activeRef : undefined}
              className={`replay-timeline-item training-timeline-phase ${selectedPhase === 'ecart' ? 'active' : ''}`}
              style={{ cursor: onPhaseClick ? 'pointer' : undefined }}
              onClick={() => onPhaseClick?.('ecart')}
            >
              <span className="replay-timeline-item-label">Écart</span>
              <div className="replay-timeline-special">
                {ecartCards!.map((card, idx) => (
                  <MiniCard key={`ecart-${card}-${idx}`} card={card} />
                ))}
              </div>
            </div>
          )}
          {tricks.map((trick, idx) => {
            const isSelected = selectedTrickNumber === trick.trickNumber;
            const isLast = idx === tricks.length - 1;
            const needsRef = isSelected || (isLast && !isReviewing && !showCurrentTrick);
            const trickMetricHighlight = metricHighlightTrickNumbers?.has(trick.trickNumber) ?? false;
            const trickBifurcation = bifurcationTrickNumbers?.has(trick.trickNumber) ?? false;
            return (
              <div
                key={trick.trickNumber}
                ref={needsRef ? activeRef : undefined}
                className={`replay-timeline-item ${isSelected ? 'active' : ''} ${trickMetricHighlight ? 'training-metric-trick-highlight' : ''} ${trickBifurcation ? 'training-bifurcation-trick' : ''}`}
                style={{ cursor: onTrickClick ? 'pointer' : undefined }}
                onClick={() => onTrickClick?.(trick.trickNumber)}
                onContextMenu={onTrickContextMenu ? (e) => { e.preventDefault(); onTrickContextMenu(trick.trickNumber, e); } : undefined}
                title={trickBifurcation ? 'Bifurcation : une meilleure ligne était possible ici' : undefined}
              >
                <span className="replay-timeline-item-label">
                  Pli {trick.trickNumber}
                  {trickBifurcation ? <span className="training-bifurcation-badge">bif</span> : null}
                </span>
                <div className="replay-timeline-thumbnail">
                  {trick.cards.map((play, i) => {
                    const screenPos = screenPositions.get(play.player) ?? 'bottom';
                    const metricGlow =
                      metricHighlightKeys?.has(`${trick.trickNumber}-${i}-${play.card}`) ?? false;
                    return (
                      <MiniCard
                        key={`${play.card}-${i}`}
                        card={play.card}
                        className={`${getThumbnailClass(screenPos)}${metricGlow ? ' training-metric-drill-glow' : ''}`}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}

          {showCurrentTrick && (
            <div
              ref={!isReviewing ? activeRef : undefined}
              className={`replay-timeline-item training-timeline-current ${!isReviewing ? 'active' : ''} ${metricHighlightTrickNumbers?.has(currentTrickNumber) ? 'training-metric-trick-highlight' : ''}`}
              style={{ cursor: isReviewing ? 'pointer' : undefined }}
              onClick={isReviewing ? onCurrentTrickClick : undefined}
            >
              <span className="replay-timeline-item-label">
                Pli {currentTrickNumber} <span className="training-timeline-live">en cours</span>
              </span>
              <div className="replay-timeline-thumbnail">
                {currentTrickCards.map((play, idx) => {
                  const screenPos = screenPositions.get(play.player) ?? 'bottom';
                  const metricGlow =
                    metricHighlightKeys?.has(`${currentTrickNumber}-${idx}-${play.card}`) ?? false;
                  return (
                    <MiniCard
                      key={`current-${play.card}-${idx}`}
                      card={play.card}
                      className={`${getThumbnailClass(screenPos)}${metricGlow ? ' training-metric-drill-glow' : ''}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
